/**
 * API payload utilities for bx-forms
 *
 * Framework-agnostic payload building that works in any environment.
 */
import type {
  FormPayload,
  FormSubmissionMetadata,
  FormTracking,
} from './types';

/**
 * Build the API payload from form data and config.
 *
 * Combines base form data, client context data, user-submitted form data,
 * and optional extras into a unified FormPayload structure for API submission.
 *
 * @param options - The payload building options
 * @param options.baseFormData - Base form data (e.g., pageUrl, referrer)
 * @param options.clientData - Client-side context data (e.g., userAgent, deviceType)
 * @param options.formData - User-submitted form field values
 * @param options.apiPayloadExtras - Optional additional data to include
 * @param options.formId - Contentful form ID
 * @param options.tracking - Analytics tracking configuration
 * @param options.templateName - Form template identifier
 * @returns FormPayload ready for API submission
 *
 * @example
 * ```typescript
 * import { buildPayload } from 'bx-forms/core';
 *
 * const payload = buildPayload({
 *   baseFormData: { pageUrl: window.location.href },
 *   clientData: { userAgent: navigator.userAgent },
 *   formData: { emailAddress: 'user@example.com' },
 *   formId: 'contentful-form-id',
 *   tracking: { brand: 'capterra', pageName: 'directory' },
 *   templateName: 'eas-comparison-flow',
 * });
 * ```
 */
export const buildPayload = ({
  baseFormData,
  clientData,
  formData,
  apiPayloadExtras,
  formId,
  tracking,
  templateName,
  submissionMetadata,
}: {
  baseFormData: Record<string, unknown>;
  clientData: Record<string, unknown>;
  formData: Record<string, unknown>;
  apiPayloadExtras?: Record<string, unknown>;
  formId: string;
  tracking: FormTracking;
  templateName: string;
  submissionMetadata?: FormSubmissionMetadata;
}): FormPayload => {
  return {
    formData: {
      ...baseFormData,
      ...clientData,
      ...formData,
      ...apiPayloadExtras,
      contentfulFormId: formId,
    },
    tracking,
    formTemplate: templateName,
    formId,
    submissionMetadata,
  };
};
